// @ts-nocheck
/**
 * Header download button + popover for the unified (Full Statements) card:
 * the ONE export surface for the whole deliverable. Clicking the trigger
 * drops the familiar glass export panel BELOW the header button (same
 * visual language as the standalone P&L popover, `--below` variant) with a
 * checkbox per part of the workbook:
 *
 *   Income statement (P&L) + Balance sheet  — pre-ticked, untickable
 *   Trends                                  — pre-ticked (computed on demand)
 *   Working capital (CCC)                   — self-ticks once the period pair joins
 *   Insights / EBITDA bridge                — self-tick once their artifact exists
 *                                             (auto-generated at the end of a run)
 *
 * plus the cell-references toggle covering BOTH statements. The download
 * POSTs /agent/sessions/{parentId}/export-unified, which populates the
 * statements server-side from the children's authoritative mappings and
 * returns one workbook (full_statements_output.xlsx). Referencing is
 * strict: a missing source file 404s, the toggle flips off, and the retry
 * ships values-only (standalone-card behavior).
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Download, Loader2 } from 'lucide-react';

import {
  downloadUnifiedWorkbook,
  SourceFileMissingError,
} from '../../utils/exportExcel';
import {
  getAgentEbitdaBridge,
  getAgentInsights,
  getWorkingCapital,
} from '../../utils/agentApi';
import { getInflightJob } from '../../utils/generationJobs';
import CellReferencesToggle from './CellReferencesToggle';

// `hasPnl` / `hasBs` mark which statements this run actually produced — a
// failed child leaves its statement out, so its row starts unticked and
// disabled instead of guaranteeing a skipped-sheet notice on download.
const UnifiedExportButton = ({
  parentId,
  pnlSessionId,
  bsSessionId,
  hasPnl = true,
  hasBs = true,
  onNotice,
}) => {
  // Same three-flag open/mounted/visible machine as FinalMappingCard's
  // popover, so open AND close both animate (see the comment there).
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelVisible, setPanelVisible] = useState(false);
  // Anchor pins to the trigger's bottom-right; `notch` is the px offset from
  // the panel's RIGHT edge to the trigger's centre so the arrow keeps
  // pointing at the trigger even when viewport clamping shifts the panel.
  const [triggerPos, setTriggerPos] = useState({ top: 0, left: 0, notch: null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [enableReferencing, setEnableReferencing] = useState(true);
  // Which parts of the deliverable ride along. Statements and Trends default
  // ON; the analyses generate automatically at the end of a run
  // (prefetchAnalyses in the host card), so WC / Insights / Bridge tick
  // themselves when the availability check below finds their artifact —
  // the workbook ships complete by default, unticking is a per-download
  // opt-out.
  const [parts, setParts] = useState({
    pnl: hasPnl,
    bs: hasBs,
    trends: hasPnl,
    wc: false,
    insights: false,
    bridge: false,
  });
  // Availability of the gated parts, refreshed each time the panel opens:
  // 'unknown' | 'checking' | 'generating' | 'available' | 'missing'
  // (+ 'unjoinable' for the working-capital pack).
  const [avail, setAvail] = useState({
    wc: 'unknown',
    insights: 'unknown',
    bridge: 'unknown',
  });
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  const clearPart = (key) =>
    setParts((prev) => (prev[key] ? { ...prev, [key]: false } : prev));
  const tickPart = (key) =>
    setParts((prev) => (prev[key] ? prev : { ...prev, [key]: true }));

  // Dismiss on outside click / Escape (anchored to the trigger so its own
  // onClick doesn't double-fire).
  useEffect(() => {
    if (!panelOpen) return undefined;
    const onPointer = (e) => {
      const t = e.target;
      if (panelRef.current && panelRef.current.contains(t)) return;
      if (triggerRef.current && triggerRef.current.contains(t)) return;
      setPanelOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [panelOpen]);

  // Open/close orchestration: mount before flipping `visible` so the CSS
  // transition has a "from" state; unmount after the close animation lands.
  useEffect(() => {
    if (panelOpen) {
      setPanelMounted(true);
      const r = requestAnimationFrame(() => setPanelVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setPanelVisible(false);
    const t = setTimeout(() => setPanelMounted(false), 240);
    return () => clearTimeout(t);
  }, [panelOpen]);

  // Opening the panel starts a fresh export decision — drop any lingering
  // post-download notice on the host card.
  useEffect(() => {
    if (panelOpen) {
      onNotice?.(null);
      setDownloadError(null);
    }
  }, [panelOpen, onNotice]);

  // Availability checks each time the panel opens. Insights live on the
  // PARENT session (the unified cross-statement pass), the bridge on the
  // P&L child — matching where their tabs generate. Working capital is a
  // plain GET (no job registry): available iff the periods joined. An
  // available artifact ticks its own box (the deliverable ships complete
  // by default); any non-available outcome unticks it so a stale tick can
  // never ship a flag for a missing artifact.
  useEffect(() => {
    if (!panelOpen) return undefined;
    let active = true;

    const check = (key, jobKey, sessionId, fetchFn) => {
      if (!sessionId) {
        setAvail((prev) => ({ ...prev, [key]: 'missing' }));
        clearPart(key);
        return;
      }
      const inflight = jobKey ? getInflightJob(jobKey) : null;
      if (inflight) {
        setAvail((prev) => ({ ...prev, [key]: 'generating' }));
        clearPart(key);
        const recheck = () => {
          if (active) check(key, jobKey, sessionId, fetchFn);
        };
        inflight.then(recheck, recheck);
        return;
      }
      setAvail((prev) => ({ ...prev, [key]: 'checking' }));
      fetchFn(sessionId)
        .then((d) => {
          if (!active) return;
          setAvail((prev) => ({
            ...prev,
            [key]: d ? 'available' : 'missing',
          }));
          if (d) tickPart(key);
          else clearPart(key);
        })
        .catch(() => {
          if (!active) return;
          setAvail((prev) => ({ ...prev, [key]: 'missing' }));
          clearPart(key);
        });
    };

    const checkWc = () => {
      if (!parentId) {
        setAvail((prev) => ({ ...prev, wc: 'missing' }));
        clearPart('wc');
        return;
      }
      setAvail((prev) => ({ ...prev, wc: 'checking' }));
      getWorkingCapital(parentId)
        .then((d) => {
          if (!active) return;
          const joinable =
            d && !d.insufficient_data && (d.columns || []).length > 0;
          setAvail((prev) => ({
            ...prev,
            wc: joinable ? 'available' : 'unjoinable',
          }));
          if (joinable) {
            // The CCC pack's formulas reference both embedded statements —
            // only self-tick while both are along for the ride.
            setParts((prev) =>
              prev.pnl && prev.bs && !prev.wc ? { ...prev, wc: true } : prev,
            );
          } else {
            clearPart('wc');
          }
        })
        .catch(() => {
          if (!active) return;
          setAvail((prev) => ({ ...prev, wc: 'missing' }));
          clearPart('wc');
        });
    };

    checkWc();
    check('insights', `insights:${parentId}`, parentId, getAgentInsights);
    check(
      'bridge',
      `ebitda-bridge:${pnlSessionId}`,
      pnlSessionId,
      getAgentEbitdaBridge,
    );
    return () => {
      active = false;
    };
  }, [panelOpen, parentId, pnlSessionId]);

  // Pin the panel below the trigger, right-aligned to its right edge. The
  // panel renders in a position:fixed anchor so it escapes .fmc-card's
  // overflow:hidden; re-measured on scroll/resize and when the panel's own
  // size settles. Clamped so it never runs off the left viewport edge.
  useLayoutEffect(() => {
    if (!panelMounted) return undefined;
    const update = () => {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 16;
      const panelW = panelRef.current ? panelRef.current.offsetWidth : 0;
      const top = r.bottom + 12;
      // The panel's RIGHT edge sits at `left` (CSS translateX(-100%)).
      let left = r.right;
      let notch = null;
      if (panelW > 0) {
        left = Math.max(left, margin + panelW);
        left = Math.min(left, window.innerWidth - margin);
        // Offset from the panel's right edge back to the trigger's centre,
        // kept clear of the rounded corners.
        notch = Math.min(
          Math.max(left - (r.left + r.width / 2), 18),
          panelW - 18,
        );
      }
      setTriggerPos((prev) => {
        if (prev.top === top && prev.left === left && prev.notch === notch)
          return prev;
        return { top, left, notch };
      });
    };
    update();
    const ro =
      typeof ResizeObserver !== 'undefined' && panelRef.current
        ? new ResizeObserver(update)
        : null;
    if (ro) ro.observe(panelRef.current);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [panelMounted]);

  const bothStatements = parts.pnl && parts.bs;
  const anyStatement = parts.pnl || parts.bs;

  const togglePart = (key, next) => {
    setParts((prev) => {
      const out = { ...prev, [key]: next };
      // The CCC pack's formulas reference both embedded statements.
      if ((key === 'pnl' || key === 'bs') && !next) out.wc = false;
      return out;
    });
  };

  const handleDownload = async () => {
    if (!anyStatement || !parentId) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const result = await downloadUnifiedWorkbook(parentId, {
        includePnl: parts.pnl,
        includeBs: parts.bs,
        includeTrends: parts.trends,
        includeWorkingCapital: parts.wc,
        includeInsights: parts.insights,
        includeEbitdaBridge: parts.bridge,
        enableReferencing,
      });
      setPanelOpen(false);
      // A ticked part the backend couldn't build (artifact invalidated
      // between the panel check and the click, or the join fell apart) is
      // skipped, not fatal — surface which ones were left out.
      const skipped = [];
      if (parts.wc && result?.sheets?.ccc === false)
        skipped.push('Working Capital');
      if (parts.trends && result?.sheets?.trends === false)
        skipped.push('Trends');
      if (parts.insights && result?.sheets?.insights === false)
        skipped.push('Insights');
      if (parts.bridge && result?.sheets?.bridge === false)
        skipped.push('EBITDA Bridge');
      if (skipped.length > 0) {
        onNotice?.(
          `${skipped.join(' and ')} data was missing. Exported without ${
            skipped.length === 1 ? 'that sheet' : 'those sheets'
          }.`,
        );
      } else if (
        parts.bridge &&
        parts.pnl &&
        result?.bridgeIncluded === false
      ) {
        // The Adjustments sheet landed (it lists every identified add-back)
        // but the P&L's collapsed block is approved-only and had nothing.
        onNotice?.(
          'No approved add-backs. The P&L adjusted EBITDA block was left out; the Adjustments sheet lists all identified add-backs.',
        );
      }
    } catch (err) {
      if (err instanceof SourceFileMissingError) {
        // Flip the toggle off so a second click succeeds values-only.
        setEnableReferencing(false);
        setDownloadError(
          'Source file no longer available — cell references turned off. Click Download again to retry without them.',
        );
      } else {
        setDownloadError(err.message || 'Download failed');
      }
    } finally {
      setDownloading(false);
    }
  };

  const rows = [
    {
      key: 'pnl',
      label: 'Income statement (P&L)',
      enabled: hasPnl,
      note: hasPnl ? null : 'Not produced in this run.',
    },
    {
      key: 'bs',
      label: 'Balance sheet',
      enabled: hasBs,
      note: hasBs ? null : 'Not produced in this run.',
    },
    {
      key: 'trends',
      label: 'Trends',
      enabled: hasPnl,
      note: hasPnl ? null : 'Needs the P&L statement.',
    },
    {
      key: 'wc',
      label: 'Working capital (CCC)',
      enabled: avail.wc === 'available' && bothStatements,
      note:
        avail.wc === 'checking'
          ? 'Checking.'
          : avail.wc === 'unjoinable' || avail.wc === 'missing'
            ? 'Periods could not be joined.'
            : !bothStatements
              ? 'Needs both statements.'
              : null,
    },
    {
      key: 'insights',
      label: 'Insights',
      enabled: avail.insights === 'available',
      note:
        avail.insights === 'generating'
          ? 'Still generating.'
          : avail.insights === 'missing'
            ? 'Not generated yet. Open the tab to run it.'
            : avail.insights === 'checking'
              ? 'Checking.'
              : null,
    },
    {
      key: 'bridge',
      label: 'EBITDA bridge',
      enabled: avail.bridge === 'available',
      note:
        avail.bridge === 'generating'
          ? 'Still generating.'
          : avail.bridge === 'missing'
            ? 'Not generated yet. Open the tab to run it.'
            : avail.bridge === 'checking'
              ? 'Checking.'
              : null,
    },
  ];

  return (
    <div className={`fmc-export-wrap ${panelOpen ? 'is-open' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`fmc-btn fmc-btn-primary fmc-export-trigger ${downloading ? 'fmc-btn-loading' : ''}`}
        onClick={() => setPanelOpen((o) => !o)}
        disabled={downloading}
        aria-expanded={panelOpen}
        aria-haspopup="dialog"
      >
        {downloading ? (
          <Loader2 size={13} className="fmc-spin" />
        ) : (
          <Download size={13} />
        )}
        <span className="fmc-btn-label">
          {downloading ? 'Generating…' : 'Download .xlsx'}
        </span>
        <ChevronDown
          size={13}
          className="fmc-export-trigger-chevron"
          aria-hidden
        />
        {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
      </button>

      {panelMounted && (
        <div
          className="fmc-export-anchor fmc-export-anchor--below"
          style={{ top: triggerPos.top, left: triggerPos.left }}
        >
          <div
            ref={panelRef}
            className={`fmc-export-panel fmc-export-panel--below ${panelVisible ? 'is-open' : ''}`}
            role="dialog"
            aria-label="Download options"
            style={
              triggerPos.notch != null
                ? { ['--fmc-export-notch-right' as any]: `${triggerPos.notch}px` }
                : undefined
            }
          >
            <div className="fmc-export-sheets fmc-export-sheets--flush">
              <div className="fmc-export-sheets-title">Include in workbook</div>
              {rows.map(({ key, label, enabled, note }) => {
                const usable = enabled && !downloading;
                return (
                  <label
                    key={key}
                    className={`fmc-export-sheet-row ${usable ? '' : 'is-disabled'} ${
                      parts[key] ? 'is-checked' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="fmc-export-sheet-check"
                      checked={parts[key]}
                      disabled={!usable}
                      onChange={(e) => togglePart(key, e.target.checked)}
                    />
                    <span className="fmc-export-sheet-box" aria-hidden>
                      {parts[key] && <Check size={10} strokeWidth={3} />}
                    </span>
                    <span className="fmc-export-sheet-label">{label}</span>
                    {note && (
                      <span className="fmc-export-sheet-note">{note}</span>
                    )}
                  </label>
                );
              })}
            </div>
            <div className="fmc-export-toggle-row">
              <CellReferencesToggle
                checked={enableReferencing}
                onChange={setEnableReferencing}
                disabled={downloading}
                hint="When on, the downloaded .xlsx includes a copy of each source sheet and links populated cells back to it via formulas."
              />
            </div>
            <div className="fmc-export-note">
              {enableReferencing
                ? 'Each formula is checked against the source cell. Every included statement carries a Tie-Out sheet that re-verifies itself in Excel.'
                : 'Exporting without cell links — values only.'}
            </div>
            <button
              type="button"
              className={`fmc-export-confirm ${downloading ? 'is-loading' : ''}`}
              onClick={handleDownload}
              disabled={downloading || !anyStatement}
            >
              {downloading ? (
                <Loader2 size={14} className="fmc-spin" />
              ) : (
                <Download size={14} />
              )}
              <span>{downloading ? 'Generating…' : 'Download .xlsx'}</span>
              {downloading && <span className="fmc-btn-shimmer" aria-hidden />}
            </button>
            {!anyStatement && (
              <span className="fmc-export-error">
                Select at least one statement to export.
              </span>
            )}
            {downloadError && (
              <span className="fmc-export-error" title={downloadError}>
                {downloadError}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedExportButton;
